import {
  ArrowLeftOutlined,
  DeleteOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  ACCOUNT_TYPE_SHORT,
  amountText,
  checkTransfer,
  DEBT_DIRECTION_LABELS,
  type DebtDirection,
  describeError,
  dirhams,
  FINANCE_ERRORS,
  fullDate,
  isNegative,
  PhotoField,
  parseOperationAmount,
  present,
  sumAmounts,
  type TransferValues,
  TYPE_LABELS,
  today,
} from '@horof/shared';
import {
  Alert,
  App,
  Button,
  ConfigProvider,
  Input,
  Radio,
  Select,
  Skeleton,
  Table,
  Tag,
} from 'antd';
import { type ReactNode, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery } from 'urql';
import { financeContext, useCanWrite, useLists } from './data';
import { Field } from './dialogs';
import {
  CreateDebtDocument,
  CreateInKindDocument,
  CreateTransferDocument,
  DebtDocument,
  type DebtRow,
  DebtsDocument,
  DeleteDebtDocument,
  DeleteInKindDocument,
  DeleteTransferDocument,
  InKindDocument,
  InKindListDocument,
  type InKindRow,
  TransferDocument,
  type TransferRow,
  TransfersDocument,
  UpdateDebtDocument,
  UpdateInKindDocument,
  UpdateTransferDocument,
} from './movements-data';
import {
  DeskPanel,
  DocumentViewer,
  ReviewBox,
  useDirty,
  useSplit,
} from './panel';
import { CounterpartyPicker, ProjectPicker } from './pickers';
import { FlagButton, ReviewTrail } from './review';
import { type FitColumn, useFitColumns } from './table-fit';
import {
  KeyValues,
  ListHeader,
  Status,
  TableCard,
  useEditKey,
  ViewBar,
} from './ui';

/** A split screen: the table, and the open row's panel beside it. */
function SplitPage<T extends { id: string }>({
  title,
  hint,
  addLabel,
  rows,
  columns,
  loading,
  panel,
  open,
  extra,
  children,
}: {
  title: string;
  hint?: ReactNode;
  addLabel: string;
  rows: T[];
  columns: FitColumn<T>[];
  loading: boolean;
  panel: string | null;
  open: (id: string) => void;
  extra?: ReactNode;
  children: ReactNode;
}) {
  const canWrite = useCanWrite();
  const { ref: fitRef, fit } = useFitColumns();
  return (
    <div className="desk-split">
      <section className="desk-page" ref={fitRef}>
        <ListHeader
          title={title}
          count={loading ? undefined : rows.length}
          description={hint}
          actions={
            canWrite && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => open('new')}
              >
                {addLabel}
              </Button>
            )
          }
        />
        <TableCard toolbar={extra}>
          <Table<T>
            rowKey="id"
            size="small"
            tableLayout="fixed"
            columns={fit(columns)}
            dataSource={rows}
            loading={loading}
            pagination={false}
            scroll={{ y: 'calc(100vh - 290px)' }}
            rowClassName={(r) =>
              r.id === panel ? 'is-open is-clickable' : 'is-clickable'
            }
            onRow={(r) => ({ onClick: () => open(r.id) })}
            locale={{ emptyText: 'القائمة فارغة' }}
          />
        </TableCard>
      </section>
      {children}
    </div>
  );
}

const docIcon = (photo: { contentType: string } | null) =>
  photo ? (
    photo.contentType === 'application/pdf' ? (
      <FilePdfOutlined className="receipt-mark" />
    ) : (
      <FileImageOutlined className="receipt-mark" />
    )
  ) : null;

const reviewTag = (r: { needsReview: boolean }) =>
  r.needsReview ? <Tag color="orange">مراجعة</Tag> : null;

/** Save, report errors in Arabic, then call `then` with the new id. */
function useSaver() {
  const { message } = App.useApp();
  const [problem, setProblem] = useState<string | null>(null);
  const run = async <R,>(
    action: () => Promise<{
      error?: Parameters<typeof describeError>[0];
      data?: R;
    }>,
    done: string,
  ): Promise<R | null> => {
    setProblem(null);
    const r = await action();
    if (r.error) {
      setProblem(describeError(r.error, 'تعذّر الحفظ.', FINANCE_ERRORS));
      return null;
    }
    message.success(done);
    return r.data ?? null;
  };
  return [problem, setProblem, run] as const;
}

function useRemove() {
  const { modal, message } = App.useApp();
  return async (
    title: string,
    content: string,
    run: () => Promise<{ error?: Parameters<typeof describeError>[0] }>,
  ) => {
    const ok = await modal.confirm({
      title,
      content: `${content} لا يمكن التراجع.`,
      okText: 'احذف',
      okButtonProps: { danger: true },
      cancelText: 'رجوع',
    });
    if (!ok) return false;
    const r = await run();
    if (r.error) {
      message.error(describeError(r.error, 'تعذّر الحذف.', FINANCE_ERRORS), 6);
      return false;
    }
    message.success('حُذف');
    return true;
  };
}

function Actions({
  saving,
  disabled,
  onDelete,
  onCancel,
}: {
  saving: boolean;
  disabled?: boolean;
  onDelete?: () => void;
  /** Back to reading, dropping the changes (doc 61). */
  onCancel?: () => void;
}) {
  return (
    <div className="desk-actions">
      <Button
        type="primary"
        htmlType="submit"
        loading={saving}
        disabled={disabled}
        title="Ctrl+S"
      >
        حفظ
      </Button>
      {onCancel && <Button onClick={onCancel}>إلغاء</Button>}
      <span className="desk-actions-gap" />
      {onDelete && (
        <Button danger icon={<DeleteOutlined />} onClick={onDelete}>
          حذف
        </Button>
      )}
    </div>
  );
}

// ——— Transfers ———

export function TransfersScreen() {
  const split = useSplit();
  const [{ data, fetching }] = useQuery({
    query: TransfersDocument,
    context: useMemo(() => financeContext, []),
  });
  const rows = present(data?.accountTransfers?.nodes);
  return (
    <SplitPage<TransferRow>
      title="التحويلات"
      hint="بين الصندوق والبنك: لا يُحسب مدخولاً ولا مصروفاً. عمليتاه تتغيّران معه تلقائياً."
      addLabel="تحويل جديد"
      rows={rows}
      loading={fetching && !data}
      panel={split.panel}
      open={split.open}
      columns={[
        {
          key: 'date',
          title: 'التاريخ',
          width: 100,
          render: (_, t) => <span className="ltr-num">{t.date}</span>,
        },
        {
          key: 'accounts',
          drop: 2,
          title: 'من ← إلى',
          width: 260,
          ellipsis: true,
          render: (_, t) => (
            <span>
              {t.fromAccount?.name} <ArrowLeftOutlined /> {t.toAccount?.name}
            </span>
          ),
        },
        {
          key: 'description',
          title: 'البيان',
          ellipsis: true,
          render: (_, t) => (
            <span>
              {t.description} {reviewTag(t)}
            </span>
          ),
        },
        {
          key: 'slip',
          drop: 1,
          title: 'وصل',
          width: 64,
          render: (_, t) => docIcon(t.receiptPhoto),
        },
        {
          key: 'amount',
          title: 'المبلغ',
          width: 130,
          render: (_, t) => (
            <strong className="desk-amount">{dirhams(t.amount)}</strong>
          ),
        },
      ]}
    >
      {split.panel && (
        <TransferPanel
          key={split.panel}
          id={split.panel === 'new' ? null : split.panel}
          onClose={split.close}
          onOpen={split.open}
          onDirtyChange={split.onDirtyChange}
        />
      )}
    </SplitPage>
  );
}

const EMPTY_TRANSFER: TransferValues = {
  date: today(),
  fromAccountId: null,
  toAccountId: null,
  amount: '',
  description: '',
  receiptPhotoId: null,
  needsReview: false,
  reviewNote: '',
};

export function TransferPanel({
  id,
  onClose,
  onOpen,
  onDirtyChange,
}: {
  id: string | null;
  onClose: () => void;
  onOpen: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [{ data }] = useQuery({
    query: TransferDocument,
    variables: { id: id ?? '' },
    pause: !id,
  });
  const t = data?.accountTransfer;
  if (id && !t) {
    return (
      <DeskPanel title="التحويل" onClose={onClose}>
        {data ? (
          <Alert type="warning" showIcon message="التحويل غير موجود" />
        ) : (
          <Skeleton active paragraph={{ rows: 8 }} />
        )}
      </DeskPanel>
    );
  }
  const saved: TransferValues = t
    ? {
        date: t.date,
        fromAccountId: t.fromAccountId,
        toAccountId: t.toAccountId,
        amount: amountText(t.amount),
        description: t.description,
        receiptPhotoId: t.receiptPhotoId,
        needsReview: t.needsReview,
        reviewNote: t.reviewNote ?? '',
      }
    : EMPTY_TRANSFER;
  return (
    <TransferForm
      id={id}
      saved={saved}
      slip={t?.receiptPhoto ?? null}
      onClose={onClose}
      onOpen={onOpen}
      onDirtyChange={onDirtyChange}
    />
  );
}

function TransferForm({
  id,
  saved,
  slip,
  onClose,
  onOpen,
  onDirtyChange,
}: {
  id: string | null;
  saved: TransferValues;
  slip: { url: string; contentType: string } | null;
  onClose: () => void;
  onOpen: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const canWrite = useCanWrite();
  const [{ data: lists }] = useLists();
  const [start, setStart] = useState(saved);
  const [values, setValues] = useState(saved);
  // Read first (doc 61); a new one opens in its form.
  const [editing, setEditing] = useState(!id);
  useEditKey(!editing && canWrite, () => setEditing(true));
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [problem, , run] = useSaver();
  const [{ fetching: a }, create] = useMutation(CreateTransferDocument);
  const [{ fetching: b }, update] = useMutation(UpdateTransferDocument);
  const [, remove] = useMutation(DeleteTransferDocument);
  const del = useRemove();
  useDirty(values, start, onDirtyChange);
  const set = (patch: Partial<TransferValues>) =>
    setValues((v) => ({ ...v, ...patch }));
  const { problems, transfer } = checkTransfer(values);
  const amount = parseOperationAmount(values.amount);
  const accounts = present(lists?.financialAccounts?.nodes).map((x) => ({
    value: x.id,
    label: `${x.name} · ${ACCOUNT_TYPE_SHORT[x.type]}`,
  }));

  async function save() {
    if (!editing) return;
    if (!canWrite || uploading) return;
    setSubmitted(true);
    if (!transfer) return;
    if (id) {
      const ok = await run(
        () => update({ id, patch: transfer }, financeContext),
        'حُفظ التحويل',
      );
      if (ok) {
        setStart(values);
        setEditing(false);
      }
      return;
    }
    const r = await run(
      () => create({ transfer }, financeContext),
      `حُفظ التحويل: ${dirhams(transfer.amount)}`,
    );
    const createdId = r?.createAccountTransfer?.accountTransfer?.id;
    if (createdId) {
      setStart(values);
      // Saved: nothing to warn about when the new row opens.
      onDirtyChange(false);
      onOpen(createdId);
    }
  }

  const onDelete = id
    ? async () => {
        if (
          await del(
            'حذف التحويل',
            `${dirhams(saved.amount)} بتاريخ ${fullDate(saved.date)}، وعمليتاه معه.`,
            () => remove({ id }, financeContext),
          )
        ) {
          onDirtyChange(false);
          onClose();
        }
      }
    : undefined;

  return (
    <DeskPanel
      title={id ? `تحويل: ${dirhams(saved.amount || 0)}` : 'تحويل جديد'}
      recordId={id}
      onClose={onClose}
      onSave={save}
    >
      {!canWrite && <Alert type="info" showIcon message="للاطلاع فقط." />}
      {id && !canWrite && (
        <div className="desk-flag">
          <FlagButton kind="transfer" id={id} />
        </div>
      )}
      {slip && <DocumentViewer photo={slip} title="وصل التحويل" />}
      {!editing && id ? (
        <TransferView
          values={start}
          onEdit={canWrite ? () => setEditing(true) : undefined}
          onDelete={canWrite ? onDelete : undefined}
        />
      ) : (
        <ConfigProvider componentDisabled={!canWrite}>
          <form
            className="desk-form"
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <div className="desk-fields">
              <label htmlFor="tr-from">من حساب</label>
              <label htmlFor="tr-to">إلى حساب</label>
              <Select
                id="tr-from"
                placeholder="خرج منه المبلغ"
                value={values.fromAccountId ?? undefined}
                onChange={(fromAccountId) =>
                  set({
                    fromAccountId,
                    toAccountId:
                      values.toAccountId === fromAccountId
                        ? null
                        : values.toAccountId,
                  })
                }
                options={accounts}
              />
              <Select
                id="tr-to"
                placeholder="دخل إليه المبلغ"
                value={values.toAccountId ?? undefined}
                onChange={(toAccountId) => set({ toAccountId })}
                options={accounts.filter(
                  (x) => x.value !== values.fromAccountId,
                )}
              />
              <label htmlFor="tr-amount">المبلغ (درهم)</label>
              <label htmlFor="tr-date">التاريخ</label>
              <span>
                <Input
                  id="tr-amount"
                  autoFocus={!id}
                  className="ltr-num desk-amount-input"
                  value={values.amount}
                  onChange={(e) => set({ amount: e.target.value })}
                />
                {amount && (
                  <span className="field-hint">{dirhams(amount)}</span>
                )}
              </span>
              <Input
                id="tr-date"
                type="date"
                className="ltr-num"
                value={values.date}
                onChange={(e) => set({ date: e.target.value })}
              />
            </div>
            <label htmlFor="tr-description">البيان</label>
            <Input
              id="tr-description"
              placeholder="مثلاً: إيداع مداخيل الماء في البنك"
              value={values.description}
              onChange={(e) => set({ description: e.target.value })}
            />
            {canWrite && (
              <PhotoField
                kind="RECEIPT"
                label={slip ? 'تغيير الوصل' : 'وصل الإيداع أو السحب'}
                hint="اختياري: اسحب الملف هنا، أو الصق صورة (Ctrl+V). صورة أو PDF."
                value={
                  values.receiptPhotoId !== start.receiptPhotoId
                    ? values.receiptPhotoId
                    : null
                }
                onChange={(receiptPhotoId) =>
                  set({
                    receiptPhotoId: receiptPhotoId ?? start.receiptPhotoId,
                  })
                }
                onBusyChange={setUploading}
                pasteFromClipboard
              />
            )}
            <ReviewBox
              needsReview={values.needsReview}
              reviewNote={values.reviewNote}
              onChange={set}
            />
            {submitted && problems.length > 0 && (
              <ul className="field-error desk-problems">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            )}
            {problem && <Alert type="error" showIcon message={problem} />}
            {canWrite && (
              <Actions
                saving={a || b}
                disabled={uploading}
                onDelete={undefined}
                onCancel={
                  id
                    ? () => {
                        setValues(start);
                        setEditing(false);
                      }
                    : undefined
                }
              />
            )}
          </form>
        </ConfigProvider>
      )}
      {id && <ReviewTrail kind="transfer" id={id} />}
    </DeskPanel>
  );
}

// ——— Loans ———

/** What is left on a loan: to repay, repaid, or repaid too much (orange). */
function Outstanding({ amount }: { amount: string }) {
  if (isNegative(amount)) {
    return (
      <span className="desk-outstanding is-over">
        دُفع أكثر بـ {dirhams(amount.trim().slice(1))}
      </span>
    );
  }
  if (Number(amount) === 0) {
    return <span className="desk-outstanding is-done">سُدِّد كاملاً</span>;
  }
  return <strong className="desk-outstanding">{dirhams(amount)}</strong>;
}

export function DebtsScreen() {
  const split = useSplit();
  const [{ data, fetching }] = useQuery({
    query: DebtsDocument,
    context: useMemo(() => financeContext, []),
  });
  const rows = present(data?.debts?.nodes);
  return (
    <SplitPage<DebtRow>
      title="الديون"
      hint="المبلغ المقترض وسداده لا يُحسبان مدخولاً ولا مصروفاً."
      addLabel="قرض جديد"
      rows={rows}
      loading={fetching && !data}
      panel={split.panel}
      open={split.open}
      columns={[
        {
          key: 'date',
          title: 'التاريخ',
          width: 100,
          render: (_, d) => <span className="ltr-num">{d.date}</span>,
        },
        {
          key: 'counterparty',
          title: 'الجهة',
          ellipsis: true,
          render: (_, d) => (
            <span>
              {d.counterparty?.name ?? '—'}{' '}
              <Tag color={d.direction === 'borrowed' ? 'purple' : 'cyan'}>
                {DEBT_DIRECTION_LABELS[d.direction]}
              </Tag>
            </span>
          ),
        },
        {
          key: 'purpose',
          title: 'الغرض',
          dataIndex: 'purpose',
          ellipsis: true,
        },
        {
          key: 'amount',
          title: 'المبلغ',
          width: 130,
          render: (_, d) => dirhams(d.amount),
        },
        {
          key: 'outstanding',
          drop: 1,
          title: 'المتبقي',
          width: 170,
          render: (_, d) => <Outstanding amount={d.outstanding} />,
        },
      ]}
    >
      {split.panel && (
        <DebtPanel
          key={split.panel}
          id={split.panel === 'new' ? null : split.panel}
          onClose={split.close}
          onOpen={split.open}
          onDirtyChange={split.onDirtyChange}
        />
      )}
    </SplitPage>
  );
}

type DebtValues = {
  direction: DebtDirection;
  counterpartyId: string | null;
  amount: string;
  date: string;
  purpose: string;
  projectId: string | null;
};

function DebtPanel({
  id,
  onClose,
  onOpen,
  onDirtyChange,
}: {
  id: string | null;
  onClose: () => void;
  onOpen: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const [{ data }] = useQuery({
    query: DebtDocument,
    variables: { id: id ?? '' },
    pause: !id,
    context: useMemo(() => financeContext, []),
  });
  const d = data?.debt;
  if (id && !d) {
    return (
      <DeskPanel title="القرض" onClose={onClose}>
        {data ? (
          <Alert type="warning" showIcon message="القرض غير موجود" />
        ) : (
          <Skeleton active paragraph={{ rows: 8 }} />
        )}
      </DeskPanel>
    );
  }
  const saved: DebtValues = d
    ? {
        direction: d.direction,
        counterpartyId: d.counterpartyId,
        amount: amountText(d.amount),
        date: d.date,
        purpose: d.purpose ?? '',
        projectId: d.projectId,
      }
    : {
        direction: 'borrowed',
        counterpartyId: null,
        amount: '',
        date: today(),
        purpose: '',
        projectId: null,
      };
  const operations = present(d?.financialOperations.nodes);
  const borrowed = (d?.direction ?? 'borrowed') === 'borrowed';
  const record = (type: 'credit' | 'debit') =>
    navigate(`/operations?panel=new&debt=${id}&optype=${type}`);

  return (
    <DebtForm
      id={id}
      saved={saved}
      onClose={onClose}
      onOpen={onOpen}
      onDirtyChange={onDirtyChange}
      deletable={!!d && d.financialOperations.totalCount === 0}
    >
      {d && (
        <section className="desk-debt">
          <div className="desk-total">
            <span>المتبقي:</span>
            <Outstanding amount={d.outstanding} />
          </div>
          {canWrite && (
            <div className="desk-actions-row">
              <Button
                className="is-credit-button"
                onClick={() => record('credit')}
              >
                {borrowed ? 'تسجيل المبلغ المستلم' : 'تسجيل استرجاع'}
              </Button>
              <Button
                className="is-debit-button"
                onClick={() => record('debit')}
              >
                {borrowed ? 'تسجيل سداد' : 'تسجيل المبلغ المُسلَّم'}
              </Button>
            </div>
          )}
          <div className="field-label">عمليات القرض</div>
          {operations.length === 0 ? (
            <div className="field-hint">لا عمليات بعد.</div>
          ) : (
            <ul className="desk-mini-list">
              {operations.map((o) => (
                <li key={o.id}>
                  <Link to={`/operations?panel=${o.id}`}>
                    <span className="ltr-num">{o.date}</span>
                    <span>{o.description || TYPE_LABELS[o.type]}</span>
                    <span className={`desk-amount is-${o.type}`}>
                      {o.type === 'credit' ? '+' : '−'} {dirhams(o.amount)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </DebtForm>
  );
}

function DebtForm({
  id,
  saved,
  onClose,
  onOpen,
  onDirtyChange,
  deletable,
  children,
}: {
  id: string | null;
  saved: DebtValues;
  onClose: () => void;
  onOpen: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
  deletable: boolean;
  children: ReactNode;
}) {
  const canWrite = useCanWrite();
  const [start, setStart] = useState(saved);
  const [values, setValues] = useState(saved);
  // Read first (doc 61); a new one opens in its form.
  const [editing, setEditing] = useState(!id);
  useEditKey(!editing && canWrite, () => setEditing(true));
  const [problem, setProblem, run] = useSaver();
  const [{ fetching: a }, create] = useMutation(CreateDebtDocument);
  const [{ fetching: b }, update] = useMutation(UpdateDebtDocument);
  const [, remove] = useMutation(DeleteDebtDocument);
  const del = useRemove();
  useDirty(values, start, onDirtyChange);
  const set = (patch: Partial<DebtValues>) =>
    setValues((v) => ({ ...v, ...patch }));
  const amount = parseOperationAmount(values.amount);

  async function save() {
    if (!editing) return;
    if (!canWrite) return;
    if (!amount) return setProblem('اكتب مبلغ القرض، مثلاً 5000.');
    if (!values.date) return setProblem('اختر تاريخ القرض.');
    const fields = {
      direction: values.direction,
      counterpartyId: values.counterpartyId,
      amount,
      date: values.date,
      purpose: values.purpose.trim() || null,
      projectId: values.projectId,
    };
    if (id) {
      const ok = await run(
        () => update({ id, patch: fields }, financeContext),
        'حُفظ القرض',
      );
      if (ok) {
        setStart(values);
        setEditing(false);
      }
      return;
    }
    const r = await run(
      () => create({ debt: fields }, financeContext),
      'حُفظ القرض',
    );
    const createdId = r?.createDebt?.debt?.id;
    if (createdId) {
      setStart(values);
      // Saved: nothing to warn about when the new row opens.
      onDirtyChange(false);
      onOpen(createdId);
    }
  }

  const onDelete =
    id && deletable
      ? async () => {
          if (
            await del('حذف القرض', `${dirhams(saved.amount)}.`, () =>
              remove({ id }, financeContext),
            )
          ) {
            onDirtyChange(false);
            onClose();
          }
        }
      : undefined;

  return (
    <DeskPanel
      title={id ? `قرض: ${dirhams(saved.amount)}` : 'قرض جديد'}
      recordId={id}
      onClose={onClose}
      onSave={save}
    >
      {children}
      {!editing && id ? (
        <DebtView
          values={start}
          onEdit={canWrite ? () => setEditing(true) : undefined}
          onDelete={canWrite ? onDelete : undefined}
        />
      ) : (
        <ConfigProvider componentDisabled={!canWrite}>
          <form
            className="desk-form"
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={values.direction}
              onChange={(e) => set({ direction: e.target.value })}
              options={(['borrowed', 'lent'] as const).map((v) => ({
                value: v,
                label: DEBT_DIRECTION_LABELS[v],
              }))}
            />
            <div className="desk-fields">
              <label htmlFor="debt-counterparty">
                {values.direction === 'borrowed' ? 'المُقرض' : 'المقترض'}
              </label>
              <label htmlFor="debt-amount">المبلغ (درهم)</label>
              <CounterpartyPicker
                id="debt-counterparty"
                type={null}
                kind={values.direction === 'borrowed' ? 'lender' : 'member'}
                value={values.counterpartyId}
                onChange={(counterpartyId) => set({ counterpartyId })}
              />
              <span>
                <Input
                  id="debt-amount"
                  className="ltr-num desk-amount-input"
                  value={values.amount}
                  onChange={(e) => set({ amount: e.target.value })}
                />
                {amount && (
                  <span className="field-hint">{dirhams(amount)}</span>
                )}
              </span>
              <label htmlFor="debt-date">التاريخ</label>
              <label htmlFor="debt-project">المشروع (اختياري)</label>
              <Input
                id="debt-date"
                type="date"
                className="ltr-num"
                value={values.date}
                onChange={(e) => set({ date: e.target.value })}
              />
              <ProjectPicker
                id="debt-project"
                clearable
                keep={start.projectId}
                placeholder="المشروع"
                value={values.projectId}
                onChange={(projectId) => set({ projectId })}
              />
            </div>
            <label htmlFor="debt-purpose">الغرض</label>
            <Input
              id="debt-purpose"
              placeholder="مثلاً: شراء مضخة جديدة"
              value={values.purpose}
              onChange={(e) => set({ purpose: e.target.value })}
            />
            {problem && <Alert type="error" showIcon message={problem} />}
            {canWrite && (
              <Actions
                saving={a || b}
                onDelete={undefined}
                onCancel={
                  id
                    ? () => {
                        setValues(start);
                        setEditing(false);
                      }
                    : undefined
                }
              />
            )}
          </form>
        </ConfigProvider>
      )}
    </DeskPanel>
  );
}

// ——— In-kind contributions ———

const BASES = ['فاتورة', 'عرض أثمان', 'تقدير'];
const NOT_CASH = 'لا تدخل في الحسابات النقدية';

export function InKindScreen() {
  const split = useSplit();
  const [{ data, fetching }] = useQuery({
    query: InKindListDocument,
    context: useMemo(() => financeContext, []),
  });
  const rows = present(data?.inKindContributions?.nodes);
  return (
    <SplitPage<InKindRow>
      title="مساهمات عينية"
      hint={`قيمة قُدِّمت بلا نقود (بئر حُفر على نفقة محسن…). ${NOT_CASH}.`}
      addLabel="مساهمة جديدة"
      rows={rows}
      loading={fetching && !data}
      panel={split.panel}
      open={split.open}
      extra={
        rows.length > 0 && (
          <div className="desk-total">
            <span>مجموع القيم التقديرية:</span>
            <strong>
              {dirhams(sumAmounts(rows.map((r) => r.estimatedValue)))}
            </strong>
          </div>
        )
      }
      columns={[
        {
          key: 'date',
          title: 'التاريخ',
          width: 100,
          render: (_, k) => <span className="ltr-num">{k.date}</span>,
        },
        {
          key: 'description',
          title: 'ما قُدِّم',
          ellipsis: true,
          render: (_, k) => (
            <span>
              {k.description} {reviewTag(k)}
            </span>
          ),
        },
        {
          key: 'donor',
          drop: 3,
          title: 'المتبرع',
          width: 170,
          ellipsis: true,
          render: (_, k) => k.counterparty?.name ?? 'متبرع مجهول',
        },
        {
          key: 'basis',
          drop: 1,
          title: 'أساس التقدير',
          width: 120,
          dataIndex: 'valuationBasis',
        },
        {
          key: 'doc',
          drop: 2,
          title: 'وثيقة',
          width: 56,
          render: (_, k) => docIcon(k.documentPhoto),
        },
        {
          key: 'value',
          title: 'القيمة',
          width: 140,
          render: (_, k) => <strong>{dirhams(k.estimatedValue)}</strong>,
        },
      ]}
    >
      {split.panel && (
        <InKindPanel
          key={split.panel}
          id={split.panel === 'new' ? null : split.panel}
          onClose={split.close}
          onOpen={split.open}
          onDirtyChange={split.onDirtyChange}
        />
      )}
    </SplitPage>
  );
}

type InKindValues = {
  date: string;
  counterpartyId: string | null;
  description: string;
  estimatedValue: string;
  valuationBasis: string | null;
  projectId: string | null;
  documentPhotoId: string | null;
  needsReview: boolean;
  reviewNote: string;
};

export function InKindPanel({
  id,
  onClose,
  onOpen,
  onDirtyChange,
}: {
  id: string | null;
  onClose: () => void;
  onOpen: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [{ data }] = useQuery({
    query: InKindDocument,
    variables: { id: id ?? '' },
    pause: !id,
  });
  const k = data?.inKindContribution;
  if (id && !k) {
    return (
      <DeskPanel title="المساهمة" onClose={onClose}>
        {data ? (
          <Alert type="warning" showIcon message="المساهمة غير موجودة" />
        ) : (
          <Skeleton active paragraph={{ rows: 8 }} />
        )}
      </DeskPanel>
    );
  }
  const saved: InKindValues = k
    ? {
        date: k.date,
        counterpartyId: k.counterpartyId,
        description: k.description,
        estimatedValue: amountText(k.estimatedValue),
        valuationBasis: k.valuationBasis,
        projectId: k.projectId,
        documentPhotoId: k.documentPhotoId,
        needsReview: k.needsReview,
        reviewNote: k.reviewNote ?? '',
      }
    : {
        date: today(),
        counterpartyId: null,
        description: '',
        estimatedValue: '',
        valuationBasis: null,
        projectId: null,
        documentPhotoId: null,
        needsReview: false,
        reviewNote: '',
      };
  return (
    <InKindForm
      id={id}
      saved={saved}
      document={k?.documentPhoto ?? null}
      onClose={onClose}
      onOpen={onOpen}
      onDirtyChange={onDirtyChange}
    />
  );
}

function InKindForm({
  id,
  saved,
  document: doc,
  onClose,
  onOpen,
  onDirtyChange,
}: {
  id: string | null;
  saved: InKindValues;
  document: { url: string; contentType: string } | null;
  onClose: () => void;
  onOpen: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const canWrite = useCanWrite();
  const [start, setStart] = useState(saved);
  const [values, setValues] = useState(saved);
  // Read first (doc 61); a new one opens in its form.
  const [editing, setEditing] = useState(!id);
  useEditKey(!editing && canWrite, () => setEditing(true));
  const [uploading, setUploading] = useState(false);
  const [problem, setProblem, run] = useSaver();
  const [{ fetching: a }, create] = useMutation(CreateInKindDocument);
  const [{ fetching: b }, update] = useMutation(UpdateInKindDocument);
  const [, remove] = useMutation(DeleteInKindDocument);
  const del = useRemove();
  useDirty(values, start, onDirtyChange);
  const set = (patch: Partial<InKindValues>) =>
    setValues((v) => ({ ...v, ...patch }));
  const value = parseOperationAmount(values.estimatedValue);
  // An imported basis outside the three choices stays visible.
  const bases =
    saved.valuationBasis && !BASES.includes(saved.valuationBasis)
      ? [...BASES, saved.valuationBasis]
      : BASES;

  async function save() {
    if (!editing) return;
    if (!canWrite || uploading) return;
    if (!values.description.trim()) {
      return setProblem('اكتب ما قُدِّم، مثلاً «حفر بئر».');
    }
    if (!value) return setProblem('اكتب القيمة التقديرية، مثلاً 200000.');
    if (!values.date) return setProblem('اختر التاريخ.');
    const fields = {
      date: values.date,
      counterpartyId: values.counterpartyId,
      description: values.description.trim(),
      estimatedValue: value,
      valuationBasis: values.valuationBasis,
      projectId: values.projectId,
      documentPhotoId: values.documentPhotoId,
      needsReview: values.needsReview,
      reviewNote: values.needsReview ? values.reviewNote.trim() || null : null,
    };
    if (id) {
      const ok = await run(
        () => update({ id, patch: fields }, financeContext),
        'حُفظت المساهمة',
      );
      if (ok) {
        setStart(values);
        setEditing(false);
      }
      return;
    }
    const r = await run(
      () => create({ contribution: fields }, financeContext),
      'حُفظت المساهمة',
    );
    const createdId = r?.createInKindContribution?.inKindContribution?.id;
    if (createdId) {
      setStart(values);
      // Saved: nothing to warn about when the new row opens.
      onDirtyChange(false);
      onOpen(createdId);
    }
  }

  const onDelete = id
    ? async () => {
        if (
          await del('حذف المساهمة', `«${saved.description}».`, () =>
            remove({ id }, financeContext),
          )
        ) {
          onDirtyChange(false);
          onClose();
        }
      }
    : undefined;

  return (
    <DeskPanel
      title={
        id
          ? `مساهمة عينية: ${dirhams(saved.estimatedValue)}`
          : 'مساهمة عينية جديدة'
      }
      recordId={id}
      onClose={onClose}
      onSave={save}
    >
      <div className="field-hint">{NOT_CASH}.</div>
      {id && !canWrite && (
        <div className="desk-flag">
          <FlagButton kind="in_kind" id={id} />
        </div>
      )}
      {doc && <DocumentViewer photo={doc} title="الوثيقة" />}
      {!editing && id ? (
        <InKindView
          values={start}
          onEdit={canWrite ? () => setEditing(true) : undefined}
          onDelete={canWrite ? onDelete : undefined}
        />
      ) : (
        <ConfigProvider componentDisabled={!canWrite}>
          <form
            className="desk-form"
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <Field label="ما الذي قُدِّم؟" htmlFor="ik-description">
              <Input
                id="ik-description"
                autoFocus={!id}
                placeholder="مثلاً: حفر بئر جديد على نفقة محسن"
                value={values.description}
                onChange={(e) => set({ description: e.target.value })}
              />
            </Field>
            <div className="desk-fields">
              <label htmlFor="ik-donor">المتبرع</label>
              <label htmlFor="ik-value">القيمة التقديرية (درهم)</label>
              <CounterpartyPicker
                id="ik-donor"
                type="credit"
                kind="donor"
                placeholder="فارغ = متبرع مجهول"
                value={values.counterpartyId}
                onChange={(counterpartyId) => set({ counterpartyId })}
              />
              <span>
                <Input
                  id="ik-value"
                  className="ltr-num desk-amount-input"
                  value={values.estimatedValue}
                  onChange={(e) => set({ estimatedValue: e.target.value })}
                />
                {value && <span className="field-hint">{dirhams(value)}</span>}
              </span>
              <label htmlFor="ik-date">التاريخ</label>
              <label htmlFor="ik-project">المشروع (اختياري)</label>
              <Input
                id="ik-date"
                type="date"
                className="ltr-num"
                value={values.date}
                onChange={(e) => set({ date: e.target.value })}
              />
              <ProjectPicker
                id="ik-project"
                clearable
                keep={start.projectId}
                placeholder="المشروع"
                value={values.projectId}
                onChange={(projectId) => set({ projectId })}
              />
            </div>
            <Field label="أساس التقدير">
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={values.valuationBasis}
                onChange={(e) => set({ valuationBasis: e.target.value })}
                options={bases.map((x) => ({ value: x, label: x }))}
              />
            </Field>
            {canWrite && (
              <PhotoField
                kind="RECEIPT"
                label={doc ? 'تغيير الوثيقة' : 'الوثيقة'}
                hint="اختيارية: فاتورة أو عرض أثمان. اسحب الملف هنا أو الصق صورة (Ctrl+V)."
                value={
                  values.documentPhotoId !== start.documentPhotoId
                    ? values.documentPhotoId
                    : null
                }
                onChange={(documentPhotoId) =>
                  set({
                    documentPhotoId: documentPhotoId ?? start.documentPhotoId,
                  })
                }
                onBusyChange={setUploading}
                pasteFromClipboard
              />
            )}
            <ReviewBox
              needsReview={values.needsReview}
              reviewNote={values.reviewNote}
              onChange={set}
            />
            {problem && <Alert type="error" showIcon message={problem} />}
            {canWrite && (
              <Actions
                saving={a || b}
                disabled={uploading}
                onDelete={undefined}
                onCancel={
                  id
                    ? () => {
                        setValues(start);
                        setEditing(false);
                      }
                    : undefined
                }
              />
            )}
          </form>
        </ConfigProvider>
      )}
      {id && <ReviewTrail kind="in_kind" id={id} />}
    </DeskPanel>
  );
}

// ——— Read first (doc 61) ———

/** A name from the lists, by id. */
function useNames() {
  const [{ data: lists }] = useLists();
  return (
    kind: 'accounts' | 'projects' | 'counterparties',
    id: string | null,
  ) => {
    if (!id) return null;
    const pool: ({ id: string; name: string } | null)[] | undefined =
      kind === 'accounts'
        ? lists?.financialAccounts?.nodes
        : kind === 'projects'
          ? lists?.projects?.nodes
          : lists?.counterparties?.nodes;
    return present(pool).find((x) => x.id === id)?.name ?? null;
  };
}

function ReviewValue({ on, note }: { on: boolean; note: string }) {
  return on ? (
    <Status tone="warning">
      تحتاج مراجعة
      {note && <span className="ui-review-note">: {note}</span>}
    </Status>
  ) : (
    <Status tone="success">لا تحتاج مراجعة</Status>
  );
}

function DeleteButton({ onDelete }: { onDelete?: () => void }) {
  return onDelete ? (
    <Button danger icon={<DeleteOutlined />} onClick={onDelete}>
      حذف
    </Button>
  ) : null;
}

function TransferView({
  values: v,
  onEdit,
  onDelete,
}: {
  values: TransferValues;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const name = useNames();
  return (
    <div className="ui-stack">
      <KeyValues
        columns={2}
        items={[
          { label: 'المبلغ', value: <strong>{dirhams(v.amount)}</strong> },
          { label: 'التاريخ', value: fullDate(v.date) },
          { label: 'من حساب', value: name('accounts', v.fromAccountId) },
          { label: 'إلى حساب', value: name('accounts', v.toAccountId) },
          { label: 'البيان', value: v.description, wide: true },
          {
            label: 'المراجعة',
            wide: true,
            value: <ReviewValue on={v.needsReview} note={v.reviewNote} />,
          },
        ]}
      />
      <ViewBar onEdit={onEdit}>
        <DeleteButton onDelete={onDelete} />
      </ViewBar>
    </div>
  );
}

function DebtView({
  values: v,
  onEdit,
  onDelete,
}: {
  values: DebtValues;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const name = useNames();
  return (
    <div className="ui-stack">
      <KeyValues
        columns={2}
        items={[
          { label: 'النوع', value: DEBT_DIRECTION_LABELS[v.direction] },
          { label: 'المبلغ', value: <strong>{dirhams(v.amount)}</strong> },
          { label: 'الجهة', value: name('counterparties', v.counterpartyId) },
          { label: 'التاريخ', value: fullDate(v.date) },
          { label: 'المشروع', value: name('projects', v.projectId) },
          { label: 'الغرض', value: v.purpose, wide: true },
        ]}
      />
      <ViewBar onEdit={onEdit}>
        <DeleteButton onDelete={onDelete} />
      </ViewBar>
    </div>
  );
}

function InKindView({
  values: v,
  onEdit,
  onDelete,
}: {
  values: InKindValues;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const name = useNames();
  return (
    <div className="ui-stack">
      <KeyValues
        columns={2}
        items={[
          { label: 'ما قُدِّم', value: v.description, wide: true },
          {
            label: 'القيمة التقديرية',
            value: <strong>{dirhams(v.estimatedValue)}</strong>,
          },
          { label: 'أساس التقدير', value: v.valuationBasis },
          {
            label: 'المتبرع',
            value: name('counterparties', v.counterpartyId) ?? 'متبرع مجهول',
          },
          { label: 'التاريخ', value: fullDate(v.date) },
          { label: 'المشروع', value: name('projects', v.projectId) },
          {
            label: 'المراجعة',
            value: <ReviewValue on={v.needsReview} note={v.reviewNote} />,
          },
        ]}
      />
      <ViewBar onEdit={onEdit}>
        <DeleteButton onDelete={onDelete} />
      </ViewBar>
    </div>
  );
}
